import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { visitoriaService } from '@/services/vistoria'
import { fileService } from '@/services/file'
import type { VisitoriaData } from '@/types'
import { FileText, Trash2, Edit2, Plus, Eye, Download, Calendar } from 'lucide-react'

export function VisitoriaListPage() {
  const [vistorias, setVistorias] = useState<VisitoriaData[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)

  const loadVistorias = async () => {
    try {
      setLoading(true)
      const response = await visitoriaService.getAll()
      
      if (response.success && response.data) {
        // Verificar se é resposta paginada ou array direto
        if (Array.isArray(response.data)) {
          setVistorias(response.data)
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          const paginatedData = response.data as any
          setVistorias(paginatedData.data || [])
        } else {
          setVistorias([])
        }
      } else {
        toast.error(response.message || 'Erro ao carregar vistorias')
        setVistorias([])
      }
    } catch (error: any) {
      console.error('Erro ao carregar vistorias:', error)
      toast.error('Erro ao carregar vistorias')
      setVistorias([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir esta vistoria? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      setDeleting(id)
      const response = await visitoriaService.delete(id)
      
      if (response.success) {
        toast.success('Vistoria excluída com sucesso!')
        await loadVistorias()
      } else {
        toast.error(response.message || 'Erro ao excluir vistoria')
      }
    } catch (error: any) {
      console.error('Erro ao deletar:', error)
      toast.error('Erro ao excluir vistoria')
    } finally {
      setDeleting(null)
    }
  }

  const handleDownload = async (vistoria: VisitoriaData) => {
    if (!vistoria.file?.id) {
      toast.error('Nenhum arquivo disponível para download')
      return
    }

    try {
      setDownloading(vistoria.id_vistoria)
      const fileName = vistoria.file.client_name || `vistoria_${vistoria.id_vistoria}_arquivo`
      await fileService.downloadFileById(vistoria.file.id, fileName)
      toast.success('Download concluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao fazer download:', error)
      toast.error(error.message || 'Erro ao fazer download do arquivo')
    } finally {
      setDownloading(null)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR')
    } catch (error) {
      console.error('Erro ao formatar data:', error)
      return 'Data inválida'
    }
  }

  const hasFile = (vistoria: VisitoriaData) => {
    return vistoria.id_file && vistoria.id_file > 0 && vistoria.file
  }

  useEffect(() => {
    loadVistorias()
  }, [])

  return (
    <div className="w-full">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <FileText className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Vistorias</h1>
            <p className="text-gray-600">Gerenciar vistorias do sistema</p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => window.location.href = '/vistorias/novo'}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Vistoria
          </Button>
        </div>
      </div>

      {/* Conteúdo principal */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        ) : vistorias.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma vistoria encontrada
            </h3>
            <p className="text-gray-600 mb-4">
              Comece adicionando sua primeira vistoria
            </p>
            <Button
              onClick={() => window.location.href = '/vistorias/novo'}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Vistoria
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {vistorias.map((vistoria, index) => (
              <div
                key={vistoria.id_vistoria || index}
                className="border rounded-lg p-6 transition-colors border-gray-200 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <FileText className="w-6 h-6 text-purple-600" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {vistoria.referencia || 'Sem referência'}
                      </h3>
                      {hasFile(vistoria) && (
                        <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-700">
                          📎 Com Arquivo
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          📍 Coordenadas: {vistoria.latitude ? parseFloat(vistoria.latitude).toFixed(6) : 'N/A'}, {vistoria.longitude ? parseFloat(vistoria.longitude).toFixed(6) : 'N/A'}
                        </span>
                        {vistoria.accuracy && parseFloat(vistoria.accuracy) > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            🎯 Precisão: {parseFloat(vistoria.accuracy).toFixed(0)}m
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p><strong>Data de Cadastro:</strong></p>
                        <p className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{vistoria.data_cadastro ? formatDate(vistoria.data_cadastro) : 'N/A'}</span>
                        </p>
                      </div>
                      
                      <div>
                        <p><strong>ID da Vistoria:</strong></p>
                        <p>#{vistoria.id_vistoria || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {hasFile(vistoria) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(vistoria)}
                        loading={downloading === (vistoria.id_vistoria || 0)}
                        title="Baixar arquivo"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `/vistorias/ver/${vistoria.id_vistoria || ''}`}
                      title="Visualizar vistoria"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `/vistorias/editar/${vistoria.id_vistoria || ''}`}
                      title="Editar vistoria"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(vistoria.id_vistoria || 0)}
                      loading={deleting === (vistoria.id_vistoria || 0)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      title="Excluir vistoria"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
